import { css } from '@emotion/react';
import {
  Badge,
  Box,
  Ellipsis,
  Flex,
  Image,
  Stack,
  Tabs,
  Text,
  Title,
  Tooltip,
} from '@inflearn/ds-react';
import { useState } from 'react';
import type { GameStatResponse } from '../../../../../resources/@types/accountSchema';
import { MY_URL } from '../../../../constants/urls';
import { type GamesStatsPeriod, useGamesStatsQuery } from '../../../../queries/useGamesStatsQuery';
import { VirtualGrid, VirtualList } from '../../../common/VirtualScroll';
import type { GameType } from './types';

// 가상 스크롤 상수
const RANKING_ITEM_HEIGHT = 68; // 랭킹 아이템 높이 (padding 포함)
const FEED_ITEM_HEIGHT = 280; // 피드 아이템 높이
const FEED_GAP = 16; // 피드 그리드 간격
const CONTAINER_HEIGHT = 580; // 스크롤 컨테이너 높이

// 랜덤 문구 목록
const RANDOM_COMMENTS = [
  '메리 크리스마스!',
  '즐거운 연말 보내세요!',
  '행복한 연말 되세요!',
  '화이팅!',
];

const badges = {
  1: { emoji: '🥇', color: 'yellow', label: '1등' },
  2: { emoji: '🥈', color: 'gray', label: '2등' },
  3: { emoji: '🥉', color: 'orange', label: '3등' },
};

// entry ID를 기반으로 랜덤 문구 선택 (같은 entry는 항상 같은 문구)
const getRandomComment = (entryId: number): string => {
  return RANDOM_COMMENTS[entryId % RANDOM_COMMENTS.length];
};

export const RankingBoard = ({ gameType }: { gameType: GameType }) => {
  const [period, setPeriod] = useState<GamesStatsPeriod>('daily');
  const isSlotDressUp = gameType === 'SLOT_DRESS_UP';

  const { data, isLoading, isError } = useGamesStatsQuery({
    gameType,
    // SLOT_DRESS_UP 게임일 때는 전체 기간으로 고정
    period: isSlotDressUp ? 'all' : period,
  });
  const renderRankBadge = (rank: number) => {
    const badge = badges[rank as keyof typeof badges];
    if (!badge) {
      return (
        <Text fw={700} c="gray.6" size="lg">
          {rank}
        </Text>
      );
    }

    return (
      <Flex align="center" gap={4}>
        <Text size="xl">{badge.emoji}</Text>
      </Flex>
    );
  };

  const emptyComponent = (
    <Flex align="center" justify="center" py={40}>
      <Text size="sm" c="gray.6">
        아직 기록이 없습니다.
      </Text>
    </Flex>
  );

  const renderFeedItem = (entry: GameStatResponse & { userId?: number }) => {
    const profileUrl = entry.userId ? MY_URL.USER_PROFILE(entry.userId) : undefined;
    const commentText = entry.comment || getRandomComment(entry.id);
    const isLongComment = commentText.length > 15;

    const cardContent = (
      <Box css={feedItemStyle}>
        {entry.imageUrl && (
          <Box css={feedImageContainerStyle}>
            <Image
              src={entry.imageUrl}
              alt={`${entry.userNickname}의 캐릭터`}
              width="100%"
              height="auto"
              radius="md"
              style={{ display: 'block' }}
            />
          </Box>
        )}
        <Stack spacing={8} mt={12}>
          <Flex align="center" justify="space-between">
            <Ellipsis lineClamp={1}>
              <Text fw={600} size="sm">
                {entry.userNickname}
              </Text>
            </Ellipsis>
          </Flex>
          <Tooltip
            label={commentText}
            multiline
            maw={200}
            disabled={!isLongComment}
            position="bottom"
            withArrow
            zIndex={9999}
            withinPortal
          >
            <div>
              <Ellipsis lineClamp={1}>
                <Text size="sm" c="gray.7" h={21} style={{ lineHeight: 1.5 }}>
                  {commentText}
                </Text>
              </Ellipsis>
            </div>
          </Tooltip>
        </Stack>
      </Box>
    );

    return profileUrl ? (
      <a href={profileUrl} target="_blank" rel="noopener noreferrer" css={feedCardLinkStyle}>
        {cardContent}
      </a>
    ) : (
      cardContent
    );
  };

  const renderRankingItem = (entry: GameStatResponse, index: number) => {
    const rank = index + 1;
    const displayScore =
      gameType === 'GET_OFF_WORK' ? (entry.score / 1000).toFixed(2) : entry.score.toLocaleString();
    const scoreUnit = gameType === 'GET_OFF_WORK' ? '초' : '점';

    return (
      <Box css={rankingItemStyle(rank <= 3)}>
        <Flex align="center" gap={12}>
          <Box css={rankBadgeStyle}>{renderRankBadge(rank)}</Box>
          <Stack spacing={2} style={{ flex: 1, minWidth: 0 }}>
            <Flex align="center" gap={8}>
              <Text fw={700} size="sm" truncate>
                {entry.userNickname}
              </Text>
              {rank <= 3 && (
                <Badge size="xs" variant="light" color={badges[rank as keyof typeof badges]?.color}>
                  TOP {rank}
                </Badge>
              )}
            </Flex>
            <Text size="xs" c="gray.6" truncate>
              {entry.comment}
            </Text>
          </Stack>
          <Text fw={700} c="green.6" size="sm" style={{ whiteSpace: 'nowrap' }}>
            {displayScore}
            {scoreUnit}
          </Text>
        </Flex>
      </Box>
    );
  };

  const renderRankingList = () => {
    if (isLoading) {
      return (
        <Flex align="center" justify="center" py={40}>
          <Text size="sm" c="gray.6">
            로딩중...
          </Text>
        </Flex>
      );
    }

    if (isError || !data) {
      return (
        <Flex align="center" justify="center" py={40}>
          <Text size="sm" c="gray.6">
            데이터를 불러올 수 없습니다.
          </Text>
        </Flex>
      );
    }

    // SLOT_DRESS_UP 게임일 때 피드 스타일로 렌더링
    if (gameType === 'SLOT_DRESS_UP') {
      const feedData = data.filter((entry) => entry.imageUrl);

      return (
        <VirtualGrid
          items={feedData}
          itemHeight={FEED_ITEM_HEIGHT}
          height={CONTAINER_HEIGHT}
          columnCount={2}
          gap={FEED_GAP}
          renderItem={(entry) => renderFeedItem(entry as GameStatResponse & { userId?: number })}
          emptyComponent={emptyComponent}
        />
      );
    }

    // 다른 게임은 VirtualList로 랭킹 스타일 렌더링
    return (
      <VirtualList
        items={data}
        itemHeight={RANKING_ITEM_HEIGHT}
        height={CONTAINER_HEIGHT}
        renderItem={(entry, index) => renderRankingItem(entry, index)}
        emptyComponent={emptyComponent}
      />
    );
  };

  return (
    <Box css={rankingBoxStyle}>
      <Title order={2} size={24} ta="center" mb={20} color="gray.9">
        {isSlotDressUp ? '피드' : '명예의 전당'}
      </Title>

      {isSlotDressUp ? (
        // SLOT_DRESS_UP 게임일 때는 탭 없이 바로 리스트 표시
        renderRankingList()
      ) : (
        // 다른 게임은 기존 탭 유지
        <Tabs
          value={period}
          onTabChange={(value) => setPeriod(value as 'daily' | 'monthly' | 'all')}
          variant="pills"
          radius="xl"
        >
          <Tabs.List grow mb={20}>
            <Tabs.Tab value="daily">일간</Tabs.Tab>
            <Tabs.Tab value="monthly">월간</Tabs.Tab>
            <Tabs.Tab value="all">전체</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="daily">{renderRankingList()}</Tabs.Panel>
          <Tabs.Panel value="monthly">{renderRankingList()}</Tabs.Panel>
          <Tabs.Panel value="all">{renderRankingList()}</Tabs.Panel>
        </Tabs>
      )}
    </Box>
  );
};

// Styles
const rankingBoxStyle = css`
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow:
    0 25px 50px rgba(34, 197, 94, 0.15),
    0 10px 30px rgba(59, 130, 246, 0.1),
    0 0 0 1px rgba(34, 197, 94, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  width: 100%;
  max-width: 450px;
  height: 720px;
  overflow: hidden;
  flex-shrink: 0;

  @media (max-width: 968px) {
    max-width: 600px;
    max-height: 500px;
  }
`;

const rankingItemStyle = (isTopThree: boolean) => css`
  background: ${isTopThree ? 'linear-gradient(135deg, #fffef7 0%, #fff8e1 100%)' : '#fafafa'};
  border: ${isTopThree ? '2px solid #FFD700' : '1px solid #e0e0e0'};
  border-radius: 12px;
  padding: 12px 16px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    background: ${isTopThree ? 'linear-gradient(135deg, #fffef7 0%, #fff4d5 100%)' : '#f5f5f5'};
  }
`;

const rankBadgeStyle = css`
  min-width: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const feedItemStyle = css`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  padding: 16px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #d0d0d0;
  }
`;

const feedImageContainerStyle = css`
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: #f5f5f5;
`;

const feedCardLinkStyle = css`
  display: block;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:hover {
    transform: scale(1.02);
    transition: transform 0.2s ease;
  }
`;
